import React, { useState } from 'react'
import { Modal, Form, Input, InputNumber, Select, Button, Row, Col, Icon, message } from 'antd'
import LocationPickerModal from '../components/LocationPickerModal.jsx'
import { api } from '../../../services/index.js'

const { Option } = Select

const GenerateLocationModal = ({
    visible,
    onCancel,
    huntId,
    users,
    userId,
    onSuccess
}) => {
    const [isGenerating, setIsGenerating] = useState(false)
    const [isLocationDataModalVisible, setIsLocationDataModalVisible] = useState(false)
    const [generatedData, setGeneratedData] = useState(null)
    const [locationForm, setLocationForm] = useState({
        location: '',
        radius: null,
        groups: [],
        pain_point: ''
    })

    const resetModal = () => {
        setLocationForm({
            location: '',
            radius: null,
            groups: [],
            pain_point: ''
        })
        setGeneratedData(null)
        onCancel()
    }

    const handleGenerateLocation = async () => {
        if (!locationForm?.location?.full) {
            message.error('Please enter a location')
            return
        }
        if (locationForm.radius > 5000) {
            message.error('Radius cannot exceed 5000 meters')
            return
        }
        if (!locationForm.groups[0]?.group_name) {
            message.error('Please input group name')
            return
        }
        if (!locationForm.groups[0]?.user_ids?.length) {
            message.error('Please select at least one user')
            return
        }
        setIsGenerating(true)
        try {
            const payload = {
                treasure_hunt_id: huntId,
                location: locationForm.location?.full,
                coordinates: locationForm.location?.lat + ',' + locationForm.location?.lng,
                radius: locationForm.radius,
                pain_point: locationForm.pain_point,
                groups: locationForm.groups.map(group => ({
                    ...group,
                    user_ids: (group.user_ids || []).map(String),
                    created_by: String(userId)
                })),
            }
            const res = await api.quest.generateLocationAction(payload)
            if (res?.data) {
                const newData = res.data
                setGeneratedData(newData)

                if (onSuccess) {
                    onSuccess(newData)
                }

                message.success('Location and stops generated successfully')
                resetModal()
            }
        } catch (error) {
            console.error('Error generating location:', error)
            message.error('Failed to generate location')
        } finally {
            setIsGenerating(false)
        }
    }

    return (
        <Modal
            title="Generate Location & Stops"
            visible={visible}
            onCancel={resetModal}
            footer={[
                <Button key="cancel" className="rounded" disabled={isGenerating} onClick={resetModal}>
                    Cancel
                </Button>,
                <Button
                    key="generate"
                    type="primary"
                    className="rounded"
                    loading={isGenerating}
                    onClick={handleGenerateLocation}
                >
                    Generate
                </Button>
            ]}
            width={600}
            centered
            maskClosable={false}
        >
            <Form layout="vertical">
                <Form.Item label="Starting Location" required>
                    <LocationPickerModal
                        onLocationSelect={(location) => {
                            setLocationForm({ ...locationForm, location })
                            setIsLocationDataModalVisible(false)
                        }}
                        onClose={() => {
                            setIsLocationDataModalVisible(false)
                        }}
                        open={isLocationDataModalVisible}
                        value={locationForm.location}
                    />
                    <Input
                        onClick={() => !isGenerating && setIsLocationDataModalVisible(true)}
                        placeholder="e.g. dhl group, Charles-de-Gaulle-Straße 20, 53113 Bonn"
                        value={locationForm.location ? (locationForm.location?.town) : ''}
                        readOnly
                    />
                </Form.Item>
                <Form.Item label="Radius (meters)" required>
                    <InputNumber
                        placeholder="e.g. 1000"
                        style={{ width: '100%' }}
                        min={100}
                        max={5000}
                        value={locationForm.radius}
                        onChange={(val) => setLocationForm({ ...locationForm, radius: val })}
                    />
                </Form.Item>
                <Form.Item label="Group Name" required>
                    <Input
                        placeholder="e.g. Group A"
                        value={locationForm.groups[0]?.group_name || ''}
                        onChange={(e) => setLocationForm({ ...locationForm, groups: [{ ...locationForm.groups[0], created_by: userId, group_name: e.target.value }] })}
                    />
                </Form.Item>
                <Form.Item label="Select Users" required>
                    <Select
                        mode="multiple"
                        placeholder="Select Users"
                        onChange={(value) => setLocationForm({ ...locationForm, groups: [{ ...locationForm.groups[0], created_by: userId, user_ids: value }] })}
                        value={locationForm.groups[0]?.user_ids}
                    >
                        {users.map(user => (
                            <Option key={user.id} value={user.id}>
                                {user.first_name + ' ' + user.last_name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Pain point / Additional context">
                    <Input.TextArea
                        placeholder="Enter any pain points or context for this location..."
                        rows={3}
                        value={locationForm.pain_point}
                        onChange={(e) => setLocationForm({ ...locationForm, pain_point: e.target.value })}
                    />
                </Form.Item>
            </Form>

            {generatedData && (
                <div style={{ marginTop: '20px', padding: '15px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                    <h4 style={{ color: '#52c41a', marginBottom: '10px' }}>
                        <Icon type="check-circle" /> Generation Summary
                    </h4>
                    <Row gutter={[16, 8]}>
                        <Col span={12}>
                            <strong>Stops:</strong> {generatedData.stops?.length || 0}
                        </Col>
                        <Col span={12}>
                            <strong>Distance:</strong> {generatedData.route_summary?.total_walking_distance_meters}m
                        </Col>
                        <Col span={12}>
                            <strong>Time:</strong> ~{generatedData.route_summary?.estimated_walking_time_minutes} mins
                        </Col>
                        <Col span={24} style={{ marginTop: '8px' }}>
                            <strong>Starting At:</strong> {generatedData.starting_location?.name}
                        </Col>
                    </Row>
                </div>
            )}
        </Modal>
    )
}

export default GenerateLocationModal
